@let isFA4Material = featureToggleService.materialFA4Search$ | async;

<div class="drawer-header">
  <h2 translate data-automation-id="search-header-label">search.search</h2>

  <button
    data-automation-id="search-drawer-close"
    mhe-button
    [purpose]="ButtonPurpose.Icon"
    placement="bottom"
    [title]="'shared.close' | translate"
    [mheTooltip]="'shared.close' | translate"
    (click)="closeEvent.emit()"
  >
    <i class="dpg-icon dpg-icon-system-close"></i>
  </button>

  @if (loading) {
    <div class="searching-indicator">
      <mat-progress-bar data-automation-id="elastic-search-progress" class="rdrx-bar" mode="query"></mat-progress-bar>
    </div>
  }
</div>

<div class="drawer-body search-container">
  <div class="search-input-container" #searchInputContainer>
    <input
      type="search"
      data-automation-id="search-input-box"
      placeholder="Enter your search term"
      class="search-input form-control has-icon"
      (focus)="handleFocus(searchInput.value)"
      (input)="handleInput(searchInput.value)"
      [attr.aria-activedescendant]="activeDescendantId"
      [attr.aria-owns]="ariaOwns"
      (keydown.arrowDown)="handleArrowDown()"
      (keydown.arrowUp)="handleArrowUp()"
      (keydown.enter)="handleEnter($event)"
      (keydown.escape)="handleEscape($event)"
      #searchInput
    />
    <i aria-hidden="true" class="dpg-icon dpg-icon-search ng-star-inserted"></i>
    @if (showResults && !loading && hasSuggestions) {
      <div class="search-autocomplete-container">
        @if (suggestions) {
          @for (suggestion of suggestions; track trackByIndex($index, suggestion.term)) {
            <div
              [attr.data-automation-id]="'elastic-search-suggestion-item-' + $index"
              [class.focus]="$index === activeDescendant"
              role="option"
              [id]="'suggestion_' + $index"
              class="suggestion"
              (click)="handleSuggestionsClick(suggestion)"
            >
              <span class="sr-only" translate>elastic_search.search_suggestion</span>
              {{ suggestion.term }}
            </div>
          }
        }
        @if (topics?.length) {
          <div class="suggestion-group">
            <strong translate>elastic_search.topics</strong>
          </div>
          @for (suggestion of topics; track trackByIndex($index, suggestion.term); let topicIndex = $index) {
            <div
              [attr.data-automation-id]="'elastic-search-topic-item-' + topicIndex"
              [class.focus]="topicIndex + suggestions.length === activeDescendant"
              role="option"
              [id]="'suggestion_' + (topicIndex + suggestions.length)"
              class="suggestion"
              (click)="handleSuggestionsClick(suggestion)"
            >
              <span class="sr-only" translate>elastic_search.launchable_topic</span>
              {{ suggestion.term }}
            </div>
          }
        }
        @if (quizzes?.length) {
          <div class="suggestion-group">
            <strong translate>elastic_search.quizzes</strong>
          </div>
          @for (suggestion of quizzes; track trackByIndex($index, suggestion.term); let quizIndex = $index) {
            <div
              [attr.data-automation-id]="'elastic-search-quiz-item-' + quizIndex"
              [class.focus]="quizIndex + suggestions.length + topics.length === activeDescendant"
              role="option"
              [id]="'suggestion_' + (quizIndex + suggestions.length + topics.length)"
              class="suggestion"
              (click)="handleSuggestionsClick(suggestion)"
            >
              <span class="sr-only" translate>elastic_search.launchable_quiz</span>
              {{ suggestion.term }}
            </div>
          }
        }
      </div>
    }
  </div>
  <div class="result-count" data-automation-id="search-result-count" aria-live="polite">
    @if (totalResults > 0) {
      @if (totalResults <= 1000) {
        <div role="status" data-automation-id="elastic-search-result-status">
          {{ totalResults }}
          {{ (totalResults === 1 ? 'search.result' : 'search.results') | translate | titlecase }}
        </div>
      } @else {
        <span class="too-many">
          <span class="sr-only">{{ 'assignment.warning_title' | translate }}</span>
          {{ 'search.limited_results' | translate }}
        </span>
      }
    } @else if (nodes && totalResults === 0) {
      <p>{{ 'search.refine_search' | translate }}</p>
    }
  </div>
  <div class="results">
    @if (isFA4Material) {
      <mat-accordion togglePosition="before" multi>
        @for(group of materialNodes; track trackByIndex($index, group.title); let i = $index) {
          <mat-expansion-panel
            hideToggle
            [expanded]="group.expanded"
            [class.expanded]="group.expanded"
            (closed)="group.expanded = false"
            (opened)="group.expanded = true"
          >
            <mat-expansion-panel-header
              #header
              data-automation-id="elastic-search-result-group-header"
              (keydown.arrowLeft)="group.expanded = false"
              (keydown.arrowRight)="group.expanded = true"
              [attr.aria-label]="group.title"
            >
              <span class="chevron-icon" [class.expanded]="group.expanded"></span>

              <div class="header-content">
                @if (group.breadcrumbs?.length) {
                  <div class="dfa-breadcrumbs">
                    <span class="sr-only">{{group | breadcrumbsDescription}}</span>
                    @for (breadcrumb of group.breadcrumbs; track trackByIndex($index, breadcrumb)) {
                      <span class="dfa-material-breadcrumb" aria-hidden="true">
                        {{ breadcrumb }}
                        <i class="dpg-icon dpg-icon-right-arrow"></i>
                      </span>
                    }
                  </div>
                }
                <p>{{ group.title }}</p>
              </div>
            </mat-expansion-panel-header>

            <ul class="search-results-chapter">
              @for (item of group.childs; track trackByIndex($index, item.hash); let j = $index) {
                <li class="search-results-item">
                  <a
                    [attr.data-automation-id]="'search-result-item-' + i + '-' + j"
                    #resultLink
                    [attr.data-index]="j"
                    [attr.data-group-index]="i"
                    tabindex="0"
                    href="javascript:void(0)"
                    [innerHTML]="item.innerHTML"
                    (click)="handleResultClicked(item)"
                  > </a>
                </li>
              }
            </ul>
          </mat-expansion-panel>
        }
      </mat-accordion>
    } @else if (nodes) {
      <ngx-bonsai
        #searchBonsaiDfa
        rdrxBonsaiTabIndex
        class="rdrx"
        [nodesById]="nodes"
        [rootNodeId]="rootNodeId"
        [expandedNodes]="getExpandedNodes()"
        (expanderClick)="toggleNodeExpanded($event)"
      >
        <ng-template #treeNodeTemplate let-node>
          @if (node.innerHTML) {
            <!-- result -->
            <a
              mheStopPropagation
              rdrxBonsaiNodeSelect
              data-automation-id="search-result-item"
              href="javascript:void(0)"
              class="rdrx-node-content ahe-ui-link"
              tabindex="-1"
              [innerHTML]="node.innerHTML"
              (click)="handleResultClicked(node)"
            ></a>
          } @else {
            <!-- group header -->
            <div>
              @if (node.breadcrumbs?.length) {
                <div class="dfa-breadcrumbs">
                  <span class="sr-only">{{node | breadcrumbsDescription}}</span>
                  @for (breadcrumb of node.breadcrumbs; track trackByIndex($index, breadcrumb)) {
                    <span class="dfa-breadcrumb" aria-hidden="true">
                      {{ breadcrumb }}
                      <i class="dpg-icon dpg-icon-right-arrow"></i>
                    </span>
                  }
                </div>
              }
              <a
                mheStopPropagation
                rdrxBonsaiNodeSelect
                data-automation-id="search-result-group"
                href="javascript:void(0)"
                class="rdrx-node-content ahe-ui-link"
                tabindex="-1"
                [rdrxBonsaiNodeSelectKeyboard]="false"
                (click)="toggleNodeExpanded(node.id)"
              >
                <span aria-hidden="true">{{ node.title }}</span>
              </a>
            </div>
          }
        </ng-template>
      </ngx-bonsai>
    }
  </div>
</div>
